import {
  SkyBadge,
  SkyButton,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import type { OverviewTests } from "@shared/types/overview";
import type { TransformationGraph, DbtJobRunRow, DbtJobView } from "@shared/types/transformations";
import {
  jobTestsReading,
  lastFinishedStep,
  newerRunWords,
  orderByDependencies,
  readsTestsItRan,
  transformStepPresentation,
  type JobTestsReading,
} from "@/components/transformations/transformFormatting";
import { RetryIcon, TransformationsIcon } from "@/lib/icons";
import { ROUTES } from "@/lib/routes";
import { OverviewCard, OverviewLink, statusTone } from "./OverviewCard";
import { plural, testsBadgeColor } from "./overviewFormatting";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Transformation tests (contract §3, card 6): a headline from the latest transform step, then
 * one row per ENABLED transformation (and, #1386, a disabled one whose reading says something
 * about tests it ran, marked "· disabled"), read with the Transformations screen's own helper
 * (`jobTestsReading`), so the two screens cannot tell a different story about one model.
 *
 * Attribution is about the GRAPH's run. When the graph is not the latest step (the graph could
 * not be read) a row says what the step said, "run-level", as the Transformations grid does; it
 * never shows a per-row count the API did not send.
 *
 * #841: a step still queued or running has no outcome yet, so while one is in flight the card
 * reads the newest FINISHED step of `recentSteps` (scheduled or manual; its rows are attributed
 * only when the graph is that step, else run-level) and says the newer one is on its way, rather
 * than "Queued" over every job's "Not run".
 */

function headline(
  latest: DbtJobRunRow | null,
  inFlight: DbtJobRunRow | null,
  zone: string,
): { text: string; detail: string | null } {
  if (!latest) return { text: "No dbt job has run yet.", detail: null };
  const presented = transformStepPresentation(latest);
  // #961: a step that succeeded but ran no test (dbt's test count is 0) is not "Succeeded" on a
  // card about tests, the Transformations page says "No tests ran" for it.
  const outcome =
    latest.stepStatus === "succeeded" && latest.testsTotal === 0 ? "No tests ran" : presented.label;
  const tests: string[] = [];
  if (latest.testsSucceeded !== null && latest.testsTotal) {
    tests.push(`${plural(latest.testsSucceeded, "test")} passed`);
  }
  if (latest.testsFailed) tests.push(`${plural(latest.testsFailed, "test")} failed`);
  const when = formatInstant(
    latest.finishedAt ?? latest.startedAt ?? latest.createdAt,
    zone,
    "short",
    {
      named: true,
    },
  );
  const now = inFlight ? ` · ${newerRunWords(inFlight)}` : "";
  return {
    text: tests.length > 0 ? `${outcome} · ${tests.join(" · ")}` : outcome,
    detail: `Last transformation run ${when}${now}`,
  };
}

/** The job's own tests from the run's graph: "2 failed" with "5 passed" beside, or "5 passed". */
function GraphTests({ ok, failed }: { ok: number; failed: number }) {
  return (
    <span className="flex items-center gap-1">
      <SkyBadge appearance="tint" color={testsBadgeColor(failed)}>
        {failed > 0 ? `${failed} failed` : `${ok} passed`}
      </SkyBadge>
      {failed > 0 && ok > 0 ? (
        <SkyText size={200} color="muted">
          {`${ok} passed`}
        </SkyText>
      ) : null}
    </span>
  );
}

function runLevelTestsWords(failed: number | null): string {
  if (failed === null) return "Not run";
  if (failed > 0) return `${failed} failed`;
  return "Passed";
}

/** The run's own test count when the graph cannot attribute tests to the job. */
function RunLevelTests({ failed }: { failed: number | null }) {
  return (
    <span className="flex items-center gap-1">
      <SkyBadge appearance="tint" color={failed === null ? "subtle" : testsBadgeColor(failed)}>
        {runLevelTestsWords(failed)}
      </SkyBadge>
      <SkyText size={200} color="muted">
        run-level
      </SkyText>
    </span>
  );
}

function muted(text: string): ReactNode {
  return (
    <SkyText size={200} color="muted">
      {text}
    </SkyText>
  );
}

/**
 * A row's tests, from `jobTestsReading`, the one decision the Transformations Jobs grid reads too
 * (#1386): its own run's reading when the step did not name it, its reading in the step otherwise.
 */
function RowOutcome({ reading, zone }: { reading: JobTestsReading; zone: string }) {
  if (reading.path === "own") {
    // #1074 review: its own run's tests, read as the Transformations Jobs view reads them.
    const { own, reading: words } = reading;
    if (own && words?.kind === "counts") {
      // #1386: the counts are its own run's, so the line names that run by when it finished.
      const when = own.at ? formatInstant(own.at, zone, "short", { named: true }) : null;
      return (
        <span className="flex items-center gap-1">
          <GraphTests ok={words.ok} failed={words.failed} />
          <SkyText size={200} color="muted">
            {when ? `· ${words.scope}, ${when}` : `· ${words.scope}`}
          </SkyText>
        </span>
      );
    }
    if (words?.kind === "text") return muted(words.text);
    return muted(own ? "—" : "Not in the last run");
  }
  const step = reading.reading;
  switch (step.kind) {
    case "counts":
      return <GraphTests ok={step.ok} failed={step.failed} />;
    case "testsOff":
      // #1386: tests off, so another job's tests on its models in the same step are not its own.
      return muted("Tests off");
    case "stoppedBeforeTests":
      return muted("Not run · the run stopped before tests ran");
    case "noTests":
      return muted("No tests on its models");
    case "runLevel":
      return <RunLevelTests failed={step.failed} />;
  }
}

/**
 * Where the transformation list stands. The rows say "No dbt job is enabled." only when
 * the list was READ and holds none; a list still loading, one whose read failed, and one the
 * role may not read are each their own state, never an empty list (contract §3: an unknown is
 * never shown as none).
 */
export type TestsListState = "loading" | "error" | "ready" | "hidden";

export function TestsCard({
  tests,
  transformations,
  graph,
  recentSteps,
  listState,
  onRetryList,
  retryingList = false,
}: {
  tests: OverviewTests;
  /** Undefined until read; null when this deployment has no transformations API (a 404). */
  transformations: DbtJobView[] | null | undefined;
  graph: TransformationGraph | undefined;
  /** #841: the newest transform steps, read only while `tests.latest` is in flight. */
  recentSteps?: DbtJobRunRow[];
  listState: TestsListState;
  onRetryList: () => void;
  retryingList?: boolean;
}) {
  const zone = useDisplayTz();
  const newest = tests.latest;
  const steps = newest
    ? [newest, ...(recentSteps ?? []).filter((step) => step.runId !== newest.runId)]
    : [];
  const { shown: latest, inFlight } = lastFinishedStep(steps);
  const head = headline(latest, inFlight, zone);
  // #1386: every enabled job, and a disabled job whose reading says something about tests it ran
  // (its counts, its tests failed, not known), so the jobs that ran the tests are never left out.
  const readings = new Map(
    (transformations ?? []).map((row) => [row.id, jobTestsReading(row, latest, graph)]),
  );
  const enabled = orderByDependencies(
    (transformations ?? []).filter((row) => {
      const reading = readings.get(row.id);
      return row.enabled || (reading !== undefined && readsTestsItRan(reading));
    }),
  );

  function renderTestList(): ReactNode {
    if (listState === "hidden") return null;
    if (listState === "loading") {
      return (
        <SkySkeleton testId="overview-tests-loading">
          <div className="flex flex-col gap-2">
            <SkySkeletonItem size={20} />
            <SkySkeletonItem size={20} />
          </div>
        </SkySkeleton>
      );
    }
    if (listState === "error") {
      return (
        <div
          className="flex items-center justify-between gap-2"
          data-testid="overview-tests-list-error"
        >
          <SkyText size={200} color="muted">
            Could not load the dbt job list.
          </SkyText>
          <SkyButton
            size="small"
            icon={RetryIcon}
            loading={retryingList}
            onClick={onRetryList}
            testId="overview-tests-list-retry"
          >
            Retry
          </SkyButton>
        </div>
      );
    }
    if (enabled.length === 0) {
      return (
        <SkyText size={200} color="muted" block testId="overview-tests-none">
          No dbt job is enabled.
        </SkyText>
      );
    }
    return (
      <ul className="flex flex-col divide-y divide-border" data-testid="overview-tests-rows">
        {enabled.map((row) => (
          <li
            key={row.id}
            className="flex items-center justify-between gap-2 py-2"
            data-testid={`overview-tests-row-${row.id}`}
          >
            <SkyText size={300}>
              <OverviewLink to={`${ROUTES.transformations}/${row.id}`}>{row.name}</OverviewLink>
              {row.enabled ? null : (
                <SkyText size={200} color="muted">
                  {" · disabled"}
                </SkyText>
              )}
            </SkyText>
            <RowOutcome
              reading={readings.get(row.id) ?? jobTestsReading(row, latest, graph)}
              zone={zone}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <OverviewCard
      id="tests"
      title="Transformation tests"
      icon={TransformationsIcon}
      tone={statusTone({
        failed: latest?.stepStatus === "failed" || (latest?.testsFailed ?? 0) > 0 ? 1 : 0,
        warning: 0,
        // #961: a step that ran no test is not a pass, so no success tone over "No tests ran".
        ok: latest?.stepStatus === "succeeded" && latest.testsTotal !== 0 ? 1 : 0,
      })}
      testId="overview-tests"
      // #1364: at tablet width the full link took the room "Transformation" needs, so there it
      // reads "Open" (the rest stays in its accessible name) and the title keeps its word whole.
      action={
        <OverviewLink to={ROUTES.transformations}>
          Open<span className="sr-only lg:not-sr-only"> Transformations</span>
        </OverviewLink>
      }
    >
      <div className="flex flex-col gap-1">
        <SkyText size={300} weight="semibold" block testId="overview-tests-headline">
          {head.text}
        </SkyText>
        {head.detail ? (
          <SkyText size={200} color="muted" block>
            {head.detail}
          </SkyText>
        ) : null}
      </div>

      {renderTestList()}
    </OverviewCard>
  );
}
